import { useState, FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, Divider } from 'antd'
import { useAuth } from '../../../context/auth-context'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import PerText from '../../../components/custom_components/Text'
import PerButton from '../../../components/custom_components/Button'
import { TargetCorrectionPaymentDto, TargetCorrectionPaymentAllowedYearsDto } from '../../../types'
import { useDateTime } from '../../../shared/useDateTime'
import { TargetCorrectionPaymentModal } from './TargetCorrectionPaymentModal'
import useMutationTargetCorrectionPayment from '../../../graphql/target-correction-payment/hooks/useMutationTargetCorrectionPayment'
import { PerPaymentCycleDropDown } from '../../../components/custom_components/PaymentCycleDropDown'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import { tableActionButtons } from '../../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import { useCurrency } from '../../../context/currency-context'
import { IconIndicator } from '../../../components/custom_components/IconIndicator/IconIndicator'
import PerSpacer from '../../../components/custom_components/Spacer'

interface Props {
  meetingId: number
  showNotOnFirstOfMonthDisclaimer: boolean
  targetCorrectionPayment?: TargetCorrectionPaymentDto
  nextPaymentcycle: string
  isPaymentDisabled?: boolean
  targetCorrectionPaymentAllowedYears: TargetCorrectionPaymentAllowedYearsDto
  existingWrongTCPayments: boolean
  appraisalSeasonYear: number
}

export const EditableTargetCorrectionPayment: FC<Props> = ({
  meetingId,
  showNotOnFirstOfMonthDisclaimer,
  targetCorrectionPayment,
  nextPaymentcycle,
  targetCorrectionPaymentAllowedYears,
  existingWrongTCPayments,
  appraisalSeasonYear,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const { currency } = useCurrency()
  const { utcDateStringToMoment } = useDateTime(user)
  const [isModalOpen, toggleModalOpen] = useState<boolean>(false)
  const [isDeleting, toggleDeleting] = useState<boolean>(false)

  const { tableActionButtonsRowWidth } = tableActionButtons()
  const { saveTargetCorrectionPayment } = useMutationTargetCorrectionPayment()

  const changePaymentCycle = (utcDate: Date) => {
    if (targetCorrectionPayment && targetCorrectionPayment.resultAmount) {
      saveTargetCorrectionPayment(
        {
          meetingId: meetingId,
          comment: targetCorrectionPayment.comment,
          paymentDate: utcDate.toUTCString(),
          resultAmount: targetCorrectionPayment.resultAmount,
          id: targetCorrectionPayment.id,
          referenceYear: targetCorrectionPayment.referenceYear,
        },
        meetingId,
      )
    }
  }

  const activeTargetcorrectionPayment = targetCorrectionPayment ? targetCorrectionPayment : null
  const atLeastOneReferenceYearAvailable =
    targetCorrectionPaymentAllowedYears.currentYearAllowed || targetCorrectionPaymentAllowedYears.previousYearAllowed

  const columns = [
    {
      key: 'reason',
      title: t('appraisalMeeting.targetCorrectionPayment.reason'),
      dataIndex: 'comment',
      className: 'textAlign-left minWidth-column-xl',
      render: (dataIndex: number) => <PerText fontWeight="bold">{dataIndex}</PerText>,
    },
    {
      key: 'referenceYear',
      title: t('appraisalMeeting.targetCorrectionPayment.referenceYear'),
      dataIndex: 'referenceYear',
      className: 'textAlign-left minWidth-column-xl',
      render: (dataIndex: string) => <PerText fontWeight="bold">{dataIndex}</PerText>,
    },
    {
      key: 'monetaryPaid',
      title: t('okr.objective.monetary.toBePaid'),
      dataIndex: 'resultAmount',
      className: 'textAlign-right minWidth-column-xxl',
      render: (dataIndex: number) => {
        return dataIndex && <PerText color="primary">{formatCurrency(dataIndex, currency)}</PerText>
      },
    },
    {
      key: 'action',
      title: t('okr.objective.actions'),
      className: 'textAlign-left minWidth-column-s',
      dataIndex: 'id',
      width: tableActionButtonsRowWidth(2),
      render: () => (
        <>
          <PerTableActionButtons
            variant="bold"
            buttons={[
              {
                icon: 'edit_fill',
                color: 'primary',
                tooltip: t('tooltip.edit'),
                showOnlyWhen: atLeastOneReferenceYearAvailable,
                onClick: (e: any) => {
                  e.preventDefault()
                  toggleModalOpen(true)
                },
                isProcessing: { active: isModalOpen },
              },
              {
                icon: 'delete_fill',
                color: 'error',
                tooltip: t('tooltip.delete'),
                onClick: (e: any) => {
                  e.preventDefault()
                  toggleDeleting(true)
                  toggleModalOpen(true)
                },
                isProcessing: { active: isDeleting },
              },
            ]}
          />
        </>
      ),
    },
  ]

  const tableData: (TargetCorrectionPaymentDto & { key: string })[] =
    activeTargetcorrectionPayment === null ? [] : [{ ...activeTargetcorrectionPayment, key: activeTargetcorrectionPayment.id }]

  return (
    <>
      <TargetCorrectionPaymentModal
        open={isModalOpen}
        isDeleting={isDeleting}
        toggleOpen={toggleModalOpen}
        toggleDeleting={toggleDeleting}
        targetCorrectionPayment={activeTargetcorrectionPayment === null ? undefined : activeTargetcorrectionPayment}
        meetingId={meetingId}
        nextPaymentcycle={nextPaymentcycle}
        appraisalSeasonYear={appraisalSeasonYear}
        targetCorrectionPaymentAllowedYears={targetCorrectionPaymentAllowedYears}
      />

      <PerFlexRow justify="space-between" align="middle" mb="s">
        <PerFlexRow align="middle">
          <PerText appearance="heading" color="primary" mr={activeTargetcorrectionPayment ? 's' : undefined}>
            {t('appraisalMeeting.targetCorrectionPayment.title') +
              (activeTargetcorrectionPayment ? ' ' + t('appraisalMeeting.targetCorrectionPayment.titleOn') : '')}
          </PerText>

          {existingWrongTCPayments && (
            <>
              <IconIndicator
                icon="warning_fill"
                background="error"
                variant="clickable"
                size="l"
                tooltip={
                  activeTargetcorrectionPayment
                    ? atLeastOneReferenceYearAvailable
                      ? t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingWrongTCPaymentsForLowEmployeeToBeChanged', {
                          year: activeTargetcorrectionPayment.referenceYear,
                        })
                      : t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingWrongTCPaymentsForLowEmployeeToBeRemoved', {
                          year: activeTargetcorrectionPayment.referenceYear,
                        })
                    : ''
                }
              />
              <PerSpacer mr={activeTargetcorrectionPayment ? 's' : undefined} />
            </>
          )}

          {activeTargetcorrectionPayment && (
            <PerPaymentCycleDropDown
              value={activeTargetcorrectionPayment.paymentDate}
              earliestCycle={nextPaymentcycle}
              onChange={(utcDateChanged: string) => {
                changePaymentCycle(utcDateStringToMoment(utcDateChanged)!.toDate())
              }}
            />
          )}
        </PerFlexRow>
        {!activeTargetcorrectionPayment && (
          <PerButton
            text={t('appraisalMeeting.targetCorrectionPayment.createButton')}
            before="add_stroke"
            onClick={() => toggleModalOpen(true)}
            color="primary"
            disabled={!atLeastOneReferenceYearAvailable}
            tooltip={!atLeastOneReferenceYearAvailable ? t('appraisalMeeting.targetCorrectionPayment.paymentButtonDisabled') : ''}
          />
        )}
      </PerFlexRow>

      <PerText color="secondary" mb="s">
        {showNotOnFirstOfMonthDisclaimer
          ? t('appraisalMeeting.targetCorrectionPayment.descriptionWithNotOnFirstOfMonthDisclaimer')
          : t('appraisalMeeting.targetCorrectionPayment.description')}
      </PerText>

      {activeTargetcorrectionPayment && <Table dataSource={tableData} columns={columns} pagination={false} />}
      <Divider />
    </>
  )
}
